import {
	CheckboxGroupField,
	DateField,
	DateTimeField,
	LocalDateTimeField,
	MultiSelectField,
	RadioGroupField,
	RadioOption,
	SingleSelectField,
	TextField,
	TextareaField,
	TimeField,
	ValidatableCheckboxField,
} from "@dgs/core";
import { FC } from "react";
import { DataFieldOption, DataFieldType, DataFieldWithOptionContingentCheckResource } from "../shared/types";
import { RegistrationEmailDataField } from "./RegistrationEmailDataField";
import { RegistrationFormFileField } from "./RegistrationFormFileField";

interface Props {
	name: string;
	dataField: DataFieldWithOptionContingentCheckResource;
	required?: boolean;
	readOnly?: boolean;
	minMaxProps?: {
		min?: string;
		max?: string;
	};
}

export const RegistrationFormDataFieldBase: FC<Props> = ({ name, dataField, required, readOnly, minMaxProps = {} }) => {
	const baseProps = {
		label: dataField.label,
		name,
		required,
		readOnly,
	};
	const props = {
		...baseProps,
		description: dataField.description ?? undefined,
		placeholder: dataField.placeholder ?? undefined,
	};

	switch (dataField.type) {
		case DataFieldType.SELECT_FIELD:
			return (
				<SingleSelectField
					{...props}
					clearable={true}
					options={dataField.options}
					getLabel={(option: DataFieldOption) => option.value}
					getValue={(option: DataFieldOption) => option.id}
				/>
			);
		case DataFieldType.MULTI_SELECT_FIELD:
			return (
				<MultiSelectField
					{...props}
					options={dataField.options}
					getLabel={(option: DataFieldOption) => option.value}
					getValue={(o: DataFieldOption) => o.id}
				/>
			);
		case DataFieldType.CHECKBOX_GROUP:
			return (
				<CheckboxGroupField
					{...props}
					disabled={readOnly}
					options={dataField.options.map((x) => ({
						value: x.id,
						name: x.value,
					}))}
				/>
			);
		case DataFieldType.FILE:
			return <RegistrationFormFileField {...props} dataFieldId={dataField.id} />;
		case DataFieldType.RADIO: {
			const options: RadioOption[] = dataField.options.reduce(
				(acc, cur) => [
					...acc,
					...[
						{
							value: JSON.stringify(cur.id),
							name: cur.value,
						},
					],
				],
				[] as RadioOption[],
			);
			return <RadioGroupField {...props} options={options} disabled={readOnly} />;
		}
		case DataFieldType.DATE:
			return <DateField {...props} {...minMaxProps} />;
		case DataFieldType.TIME:
			return <TimeField {...props} />;
		case DataFieldType.DATETIME:
			return <DateTimeField {...props} {...minMaxProps} />;
		case DataFieldType.LOCAL_DATETIME:
			return <LocalDateTimeField {...props} {...minMaxProps} />;
		case DataFieldType.CHECKBOX:
			return <ValidatableCheckboxField {...baseProps} disabled={readOnly} />;
		case DataFieldType.NUMBER:
			return <TextField type="number" {...props} {...minMaxProps} />;
		case DataFieldType.TEXT:
			return <TextField {...props} />;
		case DataFieldType.EMAIL:
			return <RegistrationEmailDataField {...props} />;
		case DataFieldType.LONG_TEXT:
			return <TextareaField {...props} />;
		case DataFieldType.URL:
			return <TextField {...props} />;
		case DataFieldType.PHONE:
			return <TextField {...props} />;
		case DataFieldType.CRYPT:
			return <TextField {...props} />;
		default:
			return null;
	}
};
